//#region src/js/components/accordion.js
/**
* Custom HTML element that implements an accordion/collapsible panel component.
* Extends HTMLElement to create a web component with expandable/collapsible functionality.
* 
* @class AccordionElement
* @extends {HTMLElement}
* 
* @description
* This component manages accordion behavior including:
* - Toggle expand/collapse functionality
* - Nested accordion support (child accordions)
* - Dynamic height calculations for smooth animations
* - Smooth scrolling to active accordion
* - Prevention of click propagation for anchor links within panels
* 
* @example
* <accordion-element>
*   <button class="accordion">Click to expand</button>
*   <div class="panel" data-panel="optional-class">
*     Content goes here
*   </div>
* </accordion-element>
*/
/**
* Creates an instance of AccordionElement.
* Initializes the accordion and panel references, sets up event listeners
* for accordion toggle and prevents click propagation on anchor links.
* 
* @constructor
*/
/**
* Toggles the accordion's open/close state.
* Handles opening and closing logic including:
* - Managing active/open CSS classes
* - Calculating and setting panel max-height for animations
* - Handling nested (child) accordion height adjustments
* - Managing optional panel dataset classes on document element
* - Smooth scrolling to accordion position when opening
* 
* @method toggleAccordion
* @returns {void}
*/
var AccordionElement = class extends HTMLElement {
	/**
	* Returns the first element matching `selector` that is a descendant of
	* this element but NOT nested inside a child <accordion-element>.
	*/
	findOwn(selector) {
		for (const el of this.querySelectorAll(selector)) if (el.closest("accordion-element") === this) return el;
		return null;
	}
	initialize() {
		this.accordion = this.accordion || this.findOwn(".accordion");
		this.panel = this.panel || this.findOwn(".panel");
		this.triggerButton = this.triggerButton || this.findOwn(".accordion__trigger");
	}
	constructor() {
		super();
		this.accordion = this.findOwn(".accordion");
		this.panel = this.findOwn(".panel");
		this.trigger = this.findOwn(".accordion__intro") || this.accordion;
		this.triggerButton = this.findOwn(".accordion__trigger");
		this.triggerButton = this.findOwn(".accordion__trigger");
		if (this.panel) this.panel.inert = true;
		if (this.trigger) this.trigger.addEventListener("click", (e) => {
			e.stopPropagation();
			this.toggleAccordion();
		});
		this.querySelectorAll("a").forEach((anchor) => {
			anchor.addEventListener("click", (event) => event.stopPropagation());
		});
	}
	/**
	* Walk up the DOM and expand any ancestor `.panel` elements that are
	* already open, so they accommodate the newly revealed content.
	*/
	expandAncestorPanels(addedHeight) {
		let node = this.parentElement;
		let delta = addedHeight;
		while (node) {
			if (node.classList && node.classList.contains("panel") && node.classList.contains("open")) {
				const current = parseFloat(node.style.maxHeight) || 0;
				node.style.maxHeight = `${current + delta}px`;
				delta = current + delta;
			}
			node = node.parentElement;
		}
	}
	updateOpenPanelHeights(panel) {
		const openPanels = [];
		let currentPanel = panel;
		while (currentPanel) {
			if (currentPanel.classList.contains("panel") && currentPanel.classList.contains("open")) {
				openPanels.push(currentPanel);
				currentPanel.style.maxHeight = "none";
			}
			currentPanel = currentPanel.parentElement;
		}
		openPanels.forEach((openPanel) => {
			openPanel.style.maxHeight = `${openPanel.scrollHeight}px`;
		});
	}
	close() {
		if (!this.accordion || !this.accordion.classList.contains("active")) return;
		const removedHeight = parseFloat(this.panel.style.maxHeight) || 0;
		this.panel.style.maxHeight = "0";
		this.panel.classList.remove("open");
		this.accordion.classList.remove("active");
		if (this.triggerButton) this.triggerButton.setAttribute("aria-expanded", "false");
		this.panel.setAttribute("aria-hidden", "true");
		this.panel.inert = true;
		if (this.panel.dataset.panel) document.documentElement.classList.remove(this.panel.dataset.panel);
		this.expandAncestorPanels(-removedHeight);
	}
	toggleAccordion() {
		this.initialize();
		if (!this.accordion) return;
		if (this.accordion.classList.contains("active")) {
			const productAccordions = document.querySelector("product-accordions");
			if (productAccordions) productAccordions.closeAccordions();
			const removedHeight = parseFloat(this.panel.style.maxHeight) || 0;
			this.panel.style.maxHeight = "0";
			this.panel.classList.toggle("open");
			this.accordion.classList.remove("active");
			if (this.triggerButton) this.triggerButton.setAttribute("aria-expanded", "false");
			this.panel.setAttribute("aria-hidden", "true");
			this.panel.inert = true;
			if (this.panel.dataset.panel) document.documentElement.classList.toggle(this.panel.dataset.panel);
			this.expandAncestorPanels(-removedHeight);
		} else {
			const parent = this.parentElement;
			if (parent) parent.querySelectorAll(":scope > accordion-element").forEach((sibling) => {
				if (sibling !== this) sibling.close();
			});
			this.open();
			if (this.panel.dataset.panel) document.documentElement.classList.toggle(this.panel.dataset.panel);
			this.expandAncestorPanels(this.panel.scrollHeight);
		}
	}
	open() {
		this.initialize();
		if (!this.accordion || !this.panel || this.accordion.classList.contains("active")) return;
		this.panel.classList.add("open");
		this.accordion.classList.add("active");
		if (this.triggerButton) this.triggerButton.setAttribute("aria-expanded", "true");
		this.panel.setAttribute("aria-hidden", "false");
		this.panel.inert = false;
		const firstChildAccordion = this.panel.querySelector(":scope > accordion-element");
		if (firstChildAccordion) firstChildAccordion.open();
		this.updateOpenPanelHeights(this.panel);
		this.panel.style.maxHeight = `${this.panel.scrollHeight}px`;
		if (firstChildAccordion) requestAnimationFrame(() => {
			firstChildAccordion.open();
			this.updateOpenPanelHeights(this.panel);
		});
	}
};
customElements.define("accordion-element", AccordionElement);
//#endregion
